<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Button from 'primevue/button';
import BaseIcon from '@/components/base/BaseIcon.vue';
import type { Account } from '@/types/account';
import type { SystemAdminUserFilters } from '@/stores/systemAdminUserStore';

const props = defineProps<{
  filters: SystemAdminUserFilters;
  accounts: Account[];
  loading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:filters', filters: SystemAdminUserFilters): void;
  (e: 'clear'): void;
}>();

const search = ref(props.filters.search);
const accountId = ref<string | null>(props.filters.accountId);

let searchDebounceId: ReturnType<typeof setTimeout> | null = null;

const hasActiveFilters = computed(() => (
  props.filters.search.trim().length > 0 || Boolean(props.filters.accountId)
));

const emitFilters = () => {
  emit('update:filters', {
    search: search.value.trim(),
    accountId: accountId.value,
  });
};

watch(
  () => props.filters,
  (nextFilters) => {
    if (search.value !== nextFilters.search) search.value = nextFilters.search;
    if (accountId.value !== nextFilters.accountId) accountId.value = nextFilters.accountId;
  },
  { deep: true },
);

watch(search, () => {
  if (search.value.trim() === props.filters.search.trim()) return;
  if (searchDebounceId) clearTimeout(searchDebounceId);
  searchDebounceId = setTimeout(() => {
    emitFilters();
  }, 600);
});

watch(accountId, () => {
  if (accountId.value === props.filters.accountId) return;
  emitFilters();
});

onBeforeUnmount(() => {
  if (searchDebounceId) clearTimeout(searchDebounceId);
});
</script>

<template>
  <div class="mb-4 flex flex-col gap-3 rounded-lg border border-surface-200 bg-white p-4 shadow-sm md:flex-row md:items-end">
    <div class="flex min-w-0 flex-[0.9] flex-col gap-1">
      <label for="system-user-search" class="text-xs font-semibold text-surface-600">Buscar usuário</label>
      <span class="relative">
        <BaseIcon class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">search</BaseIcon>
        <InputText
          id="system-user-search"
          v-model="search"
          class="w-full !pl-10"
          placeholder="Nome, sobrenome ou email"
          :disabled="loading"
        />
      </span>
    </div>

    <div class="flex min-w-0 flex-col gap-1 md:w-80">
      <label for="system-user-account" class="text-xs font-semibold text-surface-600">Conta</label>
      <Select
        id="system-user-account"
        v-model="accountId"
        :options="accounts"
        optionLabel="name"
        optionValue="id"
        placeholder="Todas as contas"
        showClear
        filter
        class="w-full"
        :disabled="loading"
      />
    </div>

    <Button
      label="Limpar filtros"
      severity="secondary"
      outlined
      class="shrink-0"
      :disabled="loading || !hasActiveFilters"
      @click="emit('clear')"
    >
      <template #icon>
        <BaseIcon size="sm">filter_list_off</BaseIcon>
      </template>
    </Button>
  </div>
</template>
